import ContractWizard from '../components/ContractWizard';
import ListingWizard from '../components/ListingWizard';
import BuyerAgreementWizard from '../components/BuyerAgreementWizard';
import ChildFormWizard from '../components/ChildFormWizard';
import { canonicalFormType, displayNameForFormType, isChildFormType, PARENT_PATH_BY_TYPE, parentIdFromValues } from './formTypeIds';

function childFormSpec(formType, displayName) {
  return {
    Wizard: ChildFormWizard,
    detailPath: (id, result) => {
      const parent = result?.entity_type || 'contract';
      const path = `/${PARENT_PATH_BY_TYPE[parent] || 'contract'}/${id}`;
      const splitId = result?.split_attachment_id;
      return splitId ? `${path}?openDoc=${splitId}` : path;
    },
    refresh: 'contracts',
    entityIdFromResult(result) {
      return result?.entity_id || result?.entityId || result?.id || null;
    },
    buildLandingProps({ commonProps, alreadyDone, entityId, prefill }) {
      const values = prefill || commonProps.initialValues || {};
      return {
        ...commonProps,
        formType,
        displayName,
        parentLocked: Boolean(parentIdFromValues(values)),
        initialSuccess: alreadyDone,
        initialEntityId: entityId,
      };
    },
    buildDealProps({ userEmail, initialValues, wizardSteps, optionalWizardFields, stillNeededLabels, allowEarlySubmitAfter, onClose, onSuccess, onBackToCatalog }) {
      return {
        isOpen: true,
        mode: 'modal',
        formType,
        displayName,
        userEmail,
        parentLocked: true,
        initialValues,
        wizardSteps: wizardSteps || initialValues?.wizardSteps || [],
        optionalWizardFields: optionalWizardFields || initialValues?.optionalWizardFields || [],
        stillNeededLabels: stillNeededLabels || initialValues?.stillNeededLabels || {},
        allowEarlySubmitAfter: allowEarlySubmitAfter || initialValues?.allowEarlySubmitAfter || '',
        onClose,
        onSuccess,
        onBackToCatalog,
      };
    },
    buildDashboardProps({ userEmail, onClose, onSuccess, onBackToCatalog, wizardSteps, optionalWizardFields, stillNeededLabels, allowEarlySubmitAfter }) {
      return {
        isOpen: true,
        mode: 'modal',
        formType,
        displayName,
        userEmail,
        parentLocked: false,
        wizardSteps: wizardSteps || [],
        optionalWizardFields: optionalWizardFields || [],
        stillNeededLabels: stillNeededLabels || {},
        allowEarlySubmitAfter: allowEarlySubmitAfter || '',
        onClose,
        onSuccess,
        onBackToCatalog,
      };
    },
  };
}

export {
  FORM_TYPE_ALIASES,
  PORTAL_PATH_BY_FORM_TYPE,
  canonicalFormType,
  FORM_TYPE_CATALOG,
} from './formTypeIds';

export const WIZARD_BY_FORM_TYPE = {
  purchase_agreement: {
    Wizard: ContractWizard,
    detailPath: (id) => `/contract/${id}`,
    refresh: 'contracts',
    entityIdFromResult(result) {
      return result?.contractId || result?.contract_id || result?.data?.contract_id || null;
    },
    buildLandingProps({ commonProps, alreadyDone, prefill, entityId }) {
      return {
        ...commonProps,
        initialSubmitResult: alreadyDone ? {
          success: true,
          message: 'Contract created successfully!',
          data: {
            contract_id: entityId,
            property_address: prefill.property_address || '',
            buyer_name: prefill.buyer_name || '',
            seller_name: prefill.seller_name || '',
            purchase_price: prefill.purchase_price,
          },
        } : null,
      };
    },
    buildDashboardProps({ onClose, onBackToCatalog }) {
      return { isOpen: true, mode: 'modal', onClose, onBackToCatalog };
    },
    buildDealProps({ parentType, parentId, onClose, onBackToCatalog }) {
      return {
        isOpen: true,
        mode: 'modal',
        onClose,
        onBackToCatalog,
        initialValues: parentType === 'buyer_agreement'
          ? { buyer_agreement_id: parentId }
          : {},
      };
    },
  },
  listing_agreement: {
    Wizard: ListingWizard,
    detailPath: (id) => `/listing/${id}`,
    refresh: 'listings',
    entityIdFromResult(result) {
      return result?.listingId || result?.listing_id || result?.id || null;
    },
    buildLandingProps({ commonProps, alreadyDone, prefill, entityId }) {
      const street = prefill.street_address || '';
      const city = prefill.city || '';
      return {
        ...commonProps,
        initialSubmitResult: alreadyDone ? {
          success: true,
          listingId: entityId,
          address: `${street}${city ? `, ${city}` : ''}`,
          syncMessage: 'Listing created. Check your email for the listing confirmation with the completed agreement attached.',
        } : null,
      };
    },
    buildDashboardProps({ onClose, onBackToCatalog }) {
      return { isOpen: true, mode: 'modal', onClose, onBackToCatalog };
    },
  },
  buyer_agreement: {
    Wizard: BuyerAgreementWizard,
    portalCreatePath: '/buyer-agreements/',
    detailPath: (id) => `/buyer-agreement/${id}`,
    refresh: 'buyerAgreements',
    entityIdFromResult(result) {
      return result?.id || result?.buyer_agreement_id || result?.buyerAgreementId || null;
    },
    buildLandingProps({ commonProps, alreadyDone }) {
      return {
        ...commonProps,
        initialSuccess: alreadyDone,
      };
    },
    buildDashboardProps({ userEmail, portalOnSubmit, onClose, onSuccess, onBackToCatalog }) {
      return {
        isOpen: true,
        mode: 'modal',
        userEmail,
        portalOnSubmit,
        onSuccess,
        onClose,
        onBackToCatalog,
      };
    },
  },
};

export function wizardSpecForFormType(formType, options = {}) {
  const canonical = canonicalFormType(formType);
  if (WIZARD_BY_FORM_TYPE[canonical]) return WIZARD_BY_FORM_TYPE[canonical];
  if (options.createsEntity === false || isChildFormType(canonical, options.catalog)) {
    return childFormSpec(canonical, displayNameForFormType(canonical, options.catalog));
  }
  return null;
}
